import { useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Select,
  NumberInput,
  NumberInputField,
  Spinner,
  Badge,
  Divider,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import useApi from "hooks/useApi";
import useDebounce from "hooks/useDebounce";
import { USERS_LISTING_API, SUBSCRIPTIONS_API } from "constants/endpoints";
import { showToast } from "components/toast/Toast";

const planUserTypes = {
  tenant: "Tenant",
  buyer: "Buyer",
  agent: "Agent",
  pg_owner: "PG Owner",
  landlord: "Landlord",
  seller: "Seller",
  co_living: "Co Living",
  pg_seeker: "PG Seeker",
};

const planTypes = {
  pro: "Pro",
  pro_plus: "Pro Plus",
};

const PAYMENT_METHODS = {
  CARD: "card",
  NET_BANKING: "netbanking",
  WALLET: "wallet",
  EMI: "emi",
  UPI: "upi",
};

const CreateSubscriptionModal = ({ isOpen, onClose, planData }) => {
  const { apiCall, loading: apiLoading } = useApi();
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [transactionId, setTransactionId] = useState("");

  // Validation errors
  const [errors, setErrors] = useState({});

  const menuRef = useRef();
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const {
    _id: planId,
    user_type: planUserType,
    type: planType,
    user_id: planUserId,
  } = planData || {};

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  // Pre-fill user if available in planData
  useEffect(() => {
    if (planUserId?._id) {
      setSelectedUser(planUserId);
      setUsers([planUserId]);
    }
  }, [planUserId]);

  // Fetch users on debounced search query change
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setUsers([]);
      return;
    }

    const fetchUsers = async () => {
      try {
        const response = await apiCall.get(USERS_LISTING_API, {
          search_text: debouncedSearchQuery,
        });
        setUsers(response?.data?.users || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      }
    };

    fetchUsers();
  }, [debouncedSearchQuery]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchQuery("");
    setShowDropdown(false);
    setUsers([]);
    setErrors((prev) => ({ ...prev, user_id: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedUser?._id) {
      newErrors.user_id = "User selection is required";
    }
    if (!amount || parseFloat(amount) < 1) {
      newErrors.amount = "Amount must be at least 1";
    }
    if (!paymentMethod) {
      newErrors.paymentMethod = "Payment method is required";
    }
    if (!transactionId.trim()) {
      newErrors.transactionId = "Transaction ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const payload = {
      plan_id: planId,
      user_id: selectedUser._id,
      amount: parseFloat(amount),
      payment_method: paymentMethod,
      transaction_id: transactionId.trim(),
    };

    try {
      const response = await apiCall.post(SUBSCRIPTIONS_API, payload);
      if (response?.statusCode === 200) {
        showToast({
          message:
            response?.data?.message || "Subscription created successfully",
          success: true,
          customStyle: {
            background: "#52c41a",
            color: "#fff",
            padding: "10px",
          },
        });

        // Reset form and close modal
        setSelectedUser(null);
        setAmount("");
        setPaymentMethod("");
        setTransactionId("");
        setSearchQuery("");
        setErrors({});
        onClose();
      } else {
        throw new Error(
          response?.data?.message || "Failed to create subscription"
        );
      }
    } catch (error) {
      console.error("Error creating subscription:", error);
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Error creating subscription";

      showToast({
        message: `Error: ${errorMessage}`,
        success: false,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUserPreFilled = planUserId?._id;
  const planName = `${planUserTypes[planUserType] || ""} ${
    planTypes[planType] || ""
  } Plan`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="12px" boxShadow="lg">
        <ModalHeader pb={0}>
          <VStack align="start" spacing={3}>
            <HStack justify="space-between" width="100%">
              <Text fontSize="xl" fontWeight="700" color="#1a202c">
                Create Subscription
              </Text>
              <ModalCloseButton position="relative" />
            </HStack>
          </VStack>
        </ModalHeader>

        <ModalBody pt={4}>
          <VStack spacing={4} align="stretch">
            {/* Plan Info Card */}
            <Box
              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              p={3}
              borderRadius="8px"
              color="white"
            >
              <Text fontSize="sm" fontWeight="500" opacity={0.9} mb={0.5}>
                Plan Details
              </Text>
              <Text fontSize="lg" fontWeight="700">
                {planName}
              </Text>
            </Box>

            {/* User Selection */}
            <FormControl isInvalid={!!errors.user_id}>
              <FormLabel fontWeight="600" fontSize="sm" color="#2d3748">
                Select User
              </FormLabel>
              {!isUserPreFilled && (
                <Box position="relative" ref={menuRef}>
                  <Input
                    placeholder="Search user by name, phone..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => setShowDropdown(true)}
                    borderColor={errors.user_id ? "#e53e3e" : "#cbd5e0"}
                    _focus={{
                      borderColor: "#667eea",
                      boxShadow: "0 0 0 1px #667eea",
                    }}
                    autoComplete="off"
                  />
                  {apiLoading && (
                    <Box position="absolute" right={3} top={3}>
                      <Spinner size="sm" color="#667eea" />
                    </Box>
                  )}

                  {showDropdown && users.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="white"
                      border="1px"
                      borderColor="#e2e8f0"
                      borderRadius="8px"
                      boxShadow="0 4px 12px rgba(0,0,0,0.1)"
                      zIndex={10}
                      maxH="300px"
                      overflowY="auto"
                    >
                      {users.map((user) => (
                        <Box
                          key={user._id}
                          p={3}
                          cursor="pointer"
                          borderBottom="1px"
                          borderColor="#f7fafc"
                          _hover={{ bg: "#f7fafc" }}
                          onClick={() => handleUserSelect(user)}
                        >
                          <Text fontWeight="600" fontSize="sm" color="#1a202c">
                            {user.full_name}
                          </Text>
                          <HStack spacing={2} mt={1}>
                            <Badge fontSize="xs" colorScheme="gray">
                              {user.role}
                            </Badge>
                            <Text fontSize="xs" color="#718096">
                              {user.phone}
                            </Text>
                          </HStack>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              {selectedUser && (
                <Box
                  p={3}
                  bg="#f7fafc"
                  borderRadius="8px"
                  border="1px"
                  borderColor="#e2e8f0"
                  mt={3}
                >
                  <VStack
                    align="start"
                    justifyContent={"space-between"}
                    flexDirection={"row"}
                    display={"flex"}
                    spacing={1}
                  >
                    <Text fontWeight="600" color="#1a202c">
                      {selectedUser?.full_name}
                    </Text>
                    <HStack spacing={2}>
                      <Badge fontSize="xs" colorScheme="blue">
                        {selectedUser?.role}
                      </Badge>
                      <Text fontSize="xs" color="#718096">
                        {selectedUser?.phone}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              )}
              {errors.user_id && (
                <FormErrorMessage>{errors.user_id}</FormErrorMessage>
              )}
            </FormControl>

            <Divider />

            {/* Amount Field */}
            <FormControl isInvalid={!!errors.amount}>
              <FormLabel fontWeight="600" fontSize="sm" color="#2d3748">
                Amount{" "}
                <Text as="span" color="#e53e3e">
                  *
                </Text>
              </FormLabel>
              <NumberInput
                value={amount}
                onChange={(valueString) => {
                  setAmount(valueString);
                  setErrors((prev) => ({ ...prev, amount: "" }));
                }}
              >
                <NumberInputField
                  placeholder="Enter amount"
                  borderColor={errors.amount ? "#e53e3e" : "#cbd5e0"}
                  _focus={{
                    borderColor: "#667eea",
                    boxShadow: "0 0 0 1px #667eea",
                  }}
                />
              </NumberInput>
              {errors.amount && (
                <FormErrorMessage>{errors.amount}</FormErrorMessage>
              )}
            </FormControl>

            {/* Payment Method */}
            <FormControl isInvalid={!!errors.paymentMethod}>
              <FormLabel fontWeight="600" fontSize="sm" color="#2d3748">
                Payment Method{" "}
                <Text as="span" color="#e53e3e">
                  *
                </Text>
              </FormLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setErrors((prev) => ({ ...prev, paymentMethod: "" }));
                }}
                placeholder="Select payment method"
                borderColor={errors.paymentMethod ? "#e53e3e" : "#cbd5e0"}
                _focus={{
                  borderColor: "#667eea",
                  boxShadow: "0 0 0 1px #667eea",
                }}
              >
                {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.replace(/_/g, " ")}
                  </option>
                ))}
              </Select>
              {errors.paymentMethod && (
                <FormErrorMessage>{errors.paymentMethod}</FormErrorMessage>
              )}
            </FormControl>

            {/* Transaction ID */}
            <FormControl isInvalid={!!errors.transactionId}>
              <FormLabel fontWeight="600" fontSize="sm" color="#2d3748">
                Transaction ID{" "}
                <Text as="span" color="#e53e3e">
                  *
                </Text>
              </FormLabel>
              <Input
                value={transactionId}
                onChange={(e) => {
                  setTransactionId(e.target.value);
                  setErrors((prev) => ({ ...prev, transactionId: "" }));
                }}
                placeholder="Enter transaction ID"
                borderColor={errors.transactionId ? "#e53e3e" : "#cbd5e0"}
                _focus={{
                  borderColor: "#667eea",
                  boxShadow: "0 0 0 1px #667eea",
                }}
              />
              {errors.transactionId && (
                <FormErrorMessage>{errors.transactionId}</FormErrorMessage>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter pt={2} gap={3}>
          <Button
            variant="ghost"
            onClick={onClose}
            fontWeight="600"
            color="#4a5568"
            _hover={{ bg: "#f7fafc" }}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            color="white"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={isSubmitting || !selectedUser}
            fontWeight="600"
            _hover={{
              opacity: 0.9,
            }}
            _active={{
              opacity: 0.8,
            }}
          >
            Create Subscription
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateSubscriptionModal;
